// Copyright (c) 2024 by Audere

import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import {
  AddQCFeedbackRequest,
  LabelingRole,
  UpdateLabelRequest,
} from "../../features/api/labelApiSlice";
import {
  CompletionStatus,
  LabelTaskKey,
  getMessageText,
  getQCCorrection,
  getTaskCompletionStatus,
  isTranslatedMessageTask,
} from "../../utils/taskUtils";
import { ErrorAlert, InfoAlert, WarningAlert } from "../Alerts";
import {
  ExpandMore as ExpandMoreIcon,
  UnfoldLess as UnfoldLessIcon,
} from "@mui/icons-material";
import {
  LabelTask,
  getLabelTask,
  getQuestions,
} from "../../features/label/labelTypes";
import React, { useCallback, useEffect, useRef, useState } from "react";

import LabelingForm from "./LabelingForm";
import Message from "../Message";
import { createStyleSheet } from "../../utils/styles";
import { useAppDispatch, useAppSelector } from "../../features/store";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useLabelingSchema } from "../../hooks/useSchema";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { updateLastUpdatedTasks } from "../../features/slices/labelSlice";
import { logEvent } from "../../features/api/apiSlice";
import { LabelingEvent } from "../../features/api/logging";
import { getConversationHook } from "../../features/data/conversationUtils";

export interface MessageLabelsProps {
  task: LabelTask;
  debugEnabled?: boolean;
  onSave: (payload: UpdateLabelRequest | AddQCFeedbackRequest) => void;
}

const MessageLabels: React.FC<MessageLabelsProps> = ({
  task,
  debugEnabled,
  onSave,
}) => {
  const { conversationUID, taskID, labelTypeID } = useParams();
  const { isQC, role, email, group, isMonitor } = useCurrentUser();
  const useConversation = getConversationHook(role);
  const { conversation, isLoading, error } = useConversation(
    conversationUID ?? "", labelTypeID ?? ""
  );

  const taskStatuses = useAppSelector((state) => state.label.taskStatuses);
  const lastUpdatedTasks = useAppSelector(
    (state) => state.label.lastUpdatedTasks
  );
  const { t } = useTranslation();
  const [expandedMessage, setExpandedMessage] = useState<string | null>(null);
  const qcCorrectLabels = useAppSelector(
    (state) => state.label.qcCorrectLabels
  );
  const dispatch = useAppDispatch();
  const { labelingTasks, labelingQuestions } = useLabelingSchema();
  const hasScrolledRef = useRef(false);

  const displayFormStatus = (messageUid: string) => {
    const status = getTaskCompletionStatus(taskStatuses, task.id, messageUid);
    switch (status) {
      case CompletionStatus.complete:
        return (
          <Typography variant="caption" color="success">
            {t("tasks.complete")}
          </Typography>
        );
      case CompletionStatus.incomplete:
        return (
          <Typography variant="caption" color="warning">
            {t("tasks.incomplete")}
          </Typography>
        );
      case CompletionStatus.new:
      default:
        return (
          <Typography variant="caption" color="textDisabled">
            {t("tasks.new")}
          </Typography>
        );
    }
  };

  const handleLastUpdatedTasks = (metric: string) => {
    if (taskID) {
      dispatch(
        updateLastUpdatedTasks({
          taskId: taskID,
          metric: metric,
        })
      );
    }
  };

  const logAccordionEvent = useCallback(
    (event: LabelingEvent, messageUid: string) => {
      logEvent(dispatch, {
        event,
        data: {
          userEmail: email,
          userRole: role,
          userGroup: group,
          conversationUid: conversationUID ?? "",
          labelType: labelTypeID,
          taskID: task.id as LabelTaskKey,
          messageUid,
        },
      });
    },
    [dispatch, email, role, group, conversationUID, labelTypeID, task.id]
  );

  const shouldShowLabelingForm = (messageUID: string): boolean =>
    Boolean(
      (getQCCorrection(qcCorrectLabels, task.id, messageUID) === false &&
        isQC) ||
        isMonitor
    );

  useEffect(() => {
    const lastMessageUID = lastUpdatedTasks[taskID as LabelTaskKey];
    if (
      !hasScrolledRef.current &&
      lastMessageUID &&
      conversation?.messages.length
    ) {
      const container = document.getElementById("task-content");
      setExpandedMessage(lastMessageUID);
      if (container) {
        const element = container.querySelector(
          `[data-testid="accordion-${lastMessageUID}"]`
        );
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
          hasScrolledRef.current = true;
        }
      }
    }
  }, [lastUpdatedTasks, taskID, conversation]);
  useEffect(() => {
    return () => {
      const container = document.getElementById("task-content");
      if (container) {
        container.scrollTop = 0;
        hasScrolledRef.current = false;
      }
    };
  }, []);

  if (isLoading) return <InfoAlert message="Loading messages..." />;
  if (error) return <ErrorAlert error={error} />;
  if (!conversation) return <WarningAlert message="No conversation found." />;
  if (!taskID || getLabelTask(taskID, labelingTasks) === undefined)
    return <WarningAlert message="No active task found." />;

  return (
    <List
      aria-label="List of messages"
      disablePadding
      sx={{ minWidth: "500px" }}
    >
      {conversation.messages.map((message) => {
        const questions = getQuestions(
          task.questionIDs,
          labelingQuestions,
          message.role
        );
        const hasQuestions = questions.length > 0;

        if (!hasQuestions) {
          return (
            <ListItem key={message.message_uid} sx={styles.userMessageItem}>
              <Message message={message} />
            </ListItem>
          );
        }

        return (
          <Accordion
            key={message.message_uid}
            sx={styles.accordion}
            role="accordion"
            data-testid={`accordion-${message.message_uid}`}
            expanded={expandedMessage === message.message_uid}
            onChange={(_, isExpanded) => {
              setExpandedMessage(isExpanded ? message.message_uid : null);
              handleLastUpdatedTasks(isExpanded ? message.message_uid : "");
              logAccordionEvent(
                isExpanded
                  ? LabelingEvent.openMessageFormAccordion
                  : LabelingEvent.closeMessageFormAccordion,
                message.message_uid
              );
            }}
            square
            disableGutters
            slotProps={{
              transition: { mountOnEnter: true, unmountOnExit: true },
            }}
          >
            <AccordionSummary
              id={`panel-${message.message_uid}-header`}
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`panel-${message.message_uid}-content`}
            >
              <Message message={message} taskContent={task.content} />
              <Box sx={styles.messageFormStatus}>
                {displayFormStatus(message.message_uid)}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {isTranslatedMessageTask(task.content) && message.translation && (
                <Box sx={styles.translationText}>
                  <Typography fontWeight="bold">Translated text:</Typography>
                  <Typography>{getMessageText(message, true)}</Typography>
                </Box>
              )}
              <div style={{ display: "flex", justifyContent: "space-around" }}>
                <LabelingForm
                  task={task}
                  debugEnabled={debugEnabled}
                  messageUID={message.message_uid}
                  formProvider={LabelingRole.conversation_labeler}
                  messageRole={message.role}
                  onSave={onSave}
                />
                {shouldShowLabelingForm(message.message_uid) && (
                  <LabelingForm
                    task={task}
                    debugEnabled={debugEnabled}
                    messageUID={message.message_uid}
                    formProvider={LabelingRole.conversation_label_qc}
                    messageRole={message.role}
                    onSave={onSave}
                  />
                )}
              </div>
            </AccordionDetails>
            <AccordionActions sx={{ justifyContent: "center" }}>
              <Button
                aria-label="collapse form"
                color="info"
                variant="text"
                size="small"
                startIcon={<UnfoldLessIcon />}
                onClick={() => {
                  setExpandedMessage(null);
                  handleLastUpdatedTasks("");
                  logAccordionEvent(
                    LabelingEvent.closeMessageFormAccordion,
                    message.message_uid
                  );
                }}
              >
                Collapse form
              </Button>
            </AccordionActions>
          </Accordion>
        );
      })}
      <Box sx={styles.listEndText}>
        <Typography variant="caption" color="textDisabled">
          {t("lists.endOfMsgs")}
        </Typography>
      </Box>
    </List>
  );
};

const styles = createStyleSheet({
  userMessageItem: {
    alignContent: "center",
    border: "1px solid #ccc",
    boxShadow: "none",
  },
  accordion: {
    border: "1px solid #ccc",
    boxShadow: "none",
    "&:before": {
      display: "none",
    },
  },
  listEndText: {
    padding: 2,
    textAlign: "center",
    width: "100%",
  },
  messageFormStatus: {
    paddingRight: 1,
    paddingLeft: 5,
    alignContent: "center",
  },
  translationText: {
    pl: 8,
    mb: 1,
  },
});

export default MessageLabels;
